import {
	Button,
	Icon,
	Input,
	InputPhoneFull,
	Typography,
	handleValid
} from 'components'
import {
	GroupComponentsLinkProps,
	Icons,
	PhoneNumbersItem,
	TabMyOrganizationItem,
	TypographyTypes
} from 'interfaces'
import React, { useEffect } from 'react'
import { Controller, Path, useFieldArray, useForm } from 'react-hook-form'
import {
	PHONE_PLACEHOLDER,
	configGenaral,
	parseToDisplay,
	parseToStorage
} from 'utils'
import {
	PropsComponentsMyOrganization,
	onChangeFieldProps,
	typeFieldPhone
} from './interfaceSlide.type'

const rules = {
	title: {
		required: { value: true, message: 'Is required' }
	},
	phone: {
		required: { value: true, message: 'Is required' }
	}
}

const GroupComponents: React.FC<
	GroupComponentsLinkProps & onChangeFieldProps
> = ({
	register,
	index,
	deleteComponent,
	setValue,
	watch,
	onChangeField,
	errorCustom
}): JSX.Element => {
	const watchTitle = watch?.(`phones.${index}.title`)

	return (
		<div className="flex flex-col w-full gap-6 mb-6">
			<Input
				name={`phones.${index}.title`}
				title="Title"
				register={register}
				rules={rules.title}
				error={errorCustom?.title}
				borderFull
				limit
				cantLimit={configGenaral.maxTitleLength}
				watchValue={watchTitle}
				setValueInput={setValue}
				onChangeCustom={e => {
					const { value } = e.target
					onChangeField({ title: value })
				}}
			/>

			<InputPhoneFull
				name={`phones.${index}.phone`}
				title="Phone"
				register={register}
				rules={rules.phone}
				error={errorCustom?.code || errorCustom?.phone_number}
				placeholder={PHONE_PLACEHOLDER}
				setValueInput={(name, value, opts) => {
					setValue?.(name as Path<typeFieldPhone>, value, opts)
					const stored = parseToStorage(value)
					const dashIdx = stored.indexOf('-')
					const code = dashIdx > -1 ? stored.slice(0, dashIdx) : ''
					const phoneNumber = dashIdx > -1 ? stored.slice(dashIdx + 1) : stored
					setValue?.(`phones.${index}.code` as Path<typeFieldPhone>, code)
					setValue?.(
						`phones.${index}.phone_number` as Path<typeFieldPhone>,
						phoneNumber
					)
					onChangeField({ title: watchTitle })
				}}
			/>
			{index > 0 && (
				<div
					aria-hidden="true"
					className=" flex gap-2 cursor-pointer"
					onClick={() => deleteComponent(index)}
				>
					<Icon
						src={Icons.delete}
						className="w-[16px] h-[18px] text-gray-3"
						fillPath
					/>
					<Typography
						title="Remove Phone"
						className="text-gray-3"
						fontLeading="15/18"
						type={TypographyTypes.p}
					/>
				</div>
			)}
		</div>
	)
}

export const SlidePhones: React.FC<PropsComponentsMyOrganization> = ({
	setErrorForm,
	pos,
	watch: watchParent,
	setValue: setValueForm,
	setFieldError,
	name
}): JSX.Element => {
	const data = watchParent(
		`${name}.${pos}.component_value` as Path<{ tabs: TabMyOrganizationItem[] }>
	) as {
		phones?: {
			title: string
			phone_number: string
			code: string
			phone?: string
		}[]
	}

	const {
		control,
		register,
		watch,
		setValue,
		formState: { errors, isValid }
	} = useForm<typeFieldPhone>({
		mode: 'onChange',
		defaultValues: {
			phones: data?.phones?.map(p => ({
				...p,
				phone:
					p.code && p.phone_number
						? parseToDisplay(`${p.code}-${p.phone_number}`)
						: p.phone ?? ''
			})) ?? [{ title: '', phone_number: '', code: '', phone: '' }]
		}
	})

	const { fields, remove, append } = useFieldArray({
		control,
		name: 'phones'
	})

	const nameType = `phone-${name}-${pos}`

	useEffect(() => {
		handleValid(nameType, isValid, setFieldError)
		setErrorForm?.(!isValid)
	}, [isValid])

	return (
		<div className="flex flex-col justify-between overflow-hidden p-2">
			<div className="flex flex-col w-full overflow-y-auto max-h-[calc(100vh-130px)]">
				<div className="mr-2">
					{fields.map((component, index) => {
						return (
							<Controller
								key={`phones-${component.id}`}
								control={control}
								name={`phones.${index}`}
								render={() => {
									return (
										<GroupComponents
											register={register}
											index={index}
											errors={errors}
											deleteComponent={remove}
											watch={watch}
											setValue={setValue}
											onChangeField={() => {
												const resultData = watch().phones?.[index]
												setValueForm(
													`${name}.${pos}.component_value.phones.${index}` as Path<{
														tabs: TabMyOrganizationItem[]
													}>,
													resultData as PhoneNumbersItem
												)
											}}
											errorCustom={errors?.phones?.[index]}
										/>
									)
								}}
							/>
						)
					})}
				</div>
				<div className="w-full mt-6">
					<Button
						type="button"
						label="Add"
						small
						onClick={() =>
							append({
								title: '',
								phone_number: '',
								code: '',
								phone: ''
							})
						}
						iconLeft={Icons.plus}
					/>
				</div>
			</div>
		</div>
	)
}
